import type { components } from "../../../api/generated";
import type {
  CustomerAssignmentConfirmPayload,
  CustomerAssignmentConfirmResult,
  CustomerAssignmentRevokePayload,
  CustomerWorkbenchContactItem,
  CustomerWorkbenchContactListPage,
  CustomerWorkbenchDetail,
  CustomerWorkbenchFormPayload,
  CustomerWorkbenchItem,
  CustomerWorkbenchListParams,
  CustomerWorkbenchMetadata,
  CustomerWorkbenchNlQueryParseResult,
  CustomerWorkbenchOrganizationOption,
  CustomerWorkbenchOrganizationUnitOption,
  CustomerWorkbenchStaffOption,
} from "../types";

export type CustomerWorkbenchItemRaw = components["schemas"]["CustomerWorkbenchItem"];
export type CustomerWorkbenchListPageRaw =
  components["schemas"]["CustomerWorkbenchListPage"];
export type CustomerWorkbenchMetadataRaw =
  components["schemas"]["CustomerWorkbenchMetadata"];
export type CustomerWorkbenchNlQueryParseResultRaw =
  components["schemas"]["CustomerWorkbenchNlQueryParseResult"];
export type CustomerWorkbenchAssignmentConfirmResultRaw =
  components["schemas"]["CustomerWorkbenchAssignmentConfirmResult"];
export type CustomerWorkbenchAssignmentRevokeMutationRaw =
  components["schemas"]["CustomerWorkbenchAssignmentRevokeMutation"];
export type CustomerWorkbenchContactListPageRaw =
  components["schemas"]["CustomerWorkbenchContactListPage"];
export type CustomerWorkbenchContactItemRaw =
  components["schemas"]["CustomerWorkbenchContactItem"];
export type CustomerWorkbenchContactRaw =
  NonNullable<CustomerWorkbenchContactListPageRaw["items"]>[number];
export type CustomerWorkbenchDetailRaw = components["schemas"]["CustomerWorkbenchDetail"];
export type CustomerWorkbenchCreateMutationRaw =
  components["schemas"]["CustomerWorkbenchCreateMutation"];
export type CustomerWorkbenchDeleteResultRaw =
  components["schemas"]["CustomerWorkbenchDeleteResult"];
export type CustomerWorkbenchOrganizationOptionRaw =
  components["schemas"]["CustomerWorkbenchOrganizationOption"];
export type CustomerWorkbenchOrganizationUnitOptionRaw =
  components["schemas"]["CustomerWorkbenchOrganizationUnitOption"];

/** 映射客户工作台分页查询参数。 */
export function mapCustomerWorkbenchListQueryParams(params: CustomerWorkbenchListParams) {
  return {
    view_scope: params.viewScope,
    page: params.page,
    page_size: params.pageSize,
    region_school_text: normalizeOptionalText(params.regionSchoolText),
    customer_name: normalizeOptionalText(params.customerName),
    sales_user_id: params.salesUserId,
    primary_owner_user_id: params.primaryOwnerUserId,
    shared_owner_user_id: params.sharedOwnerUserId,
    is_disabled: params.isDisabled,
    nl_query: normalizeOptionalText(params.nlQuery),
    sort_field: params.sortField,
    sort_direction: params.sortDirection,
  };
}

/** 映射客户工作台自然语言查询解析请求。 */
export function mapCustomerWorkbenchNlQueryParsePayload(query: string) {
  return {
    query: query.trim(),
  };
}

/** 映射客户销售分配确认请求。 */
export function mapCustomerAssignmentConfirmPayload(
  payload: CustomerAssignmentConfirmPayload,
) {
  return {
    customer_ids: payload.customerIds,
    target_user_id: payload.targetUserId,
    relation_type: payload.relationType,
    conflict_strategy: payload.conflictStrategy,
    current_user_id: payload.currentUserId,
    permission_level: payload.permissionLevel,
    source_type: payload.sourceType ?? "manual",
    source_id: payload.sourceId,
    reason: payload.reason ?? "",
    source_snapshot_json: payload.sourceSnapshot,
  };
}

/** 映射撤销客户共享销售请求。 */
export function mapCustomerAssignmentRevokePayload(
  payload: CustomerAssignmentRevokePayload,
): CustomerWorkbenchAssignmentRevokeMutationRaw {
  return {
    customer_ids: payload.customerIds,
    target_user_id: payload.targetUserId,
    current_user_id: payload.currentUserId,
    reason: payload.reason ?? "",
  };
}

/** 映射客户新增/编辑请求。 */
export function mapCustomerWorkbenchFormPayload(
  payload: CustomerWorkbenchFormPayload,
): CustomerWorkbenchCreateMutationRaw {
  return {
    customer_name: payload.customerName,
    org_id: payload.orgId,
    anchor_org_unit_id: payload.anchorOrgUnitId,
    anchor_level: payload.anchorLevel,
    coverage_mode: payload.coverageMode,
    business_status: payload.businessStatus,
    customer_star: normalizeOptionalText(payload.customerStar),
    remark: normalizeOptionalText(payload.remark),
    current_user_id: payload.currentUserId,
  };
}

/** 映射客户工作台元数据。 */
export function mapCustomerWorkbenchMetadata(
  raw: CustomerWorkbenchMetadataRaw,
): CustomerWorkbenchMetadata {
  return {
    staffOptions: (raw.staff_options ?? []).map(mapCustomerWorkbenchStaffOption),
    sortOptions: (raw.sort_options ?? []).map((option) => ({
      field: option.field,
      label: option.label,
    })),
    opportunityPlaceholder: raw.opportunity_placeholder,
  };
}

/** 映射客户工作台列表项。 */
export function mapCustomerWorkbenchItem(raw: CustomerWorkbenchItemRaw): CustomerWorkbenchItem {
  return {
    id: raw.id,
    customerName: raw.customer_name,
    orgId: raw.org_id,
    orgName: raw.org_name,
    province: raw.province ?? undefined,
    city: raw.city ?? undefined,
    orgUnitId: raw.org_unit_id ?? undefined,
    orgUnitName: raw.org_unit_name ?? undefined,
    orgUnitPathName: raw.org_unit_path_name ?? undefined,
    anchorLevel: raw.anchor_level,
    businessStatus: raw.business_status,
    isDisabled: raw.is_disabled,
    primaryOwnerUserIds: raw.primary_owner_user_ids ?? [],
    primaryOwnerUserNames: raw.primary_owner_user_names ?? [],
    sharedOwnerUserIds: raw.shared_owner_user_ids ?? [],
    sharedOwnerUserNames: raw.shared_owner_user_names ?? [],
    opportunityCount: raw.opportunity_count,
    opportunityCountPlaceholder: raw.opportunity_count_placeholder,
    contactCount: raw.contact_count,
    accessRole: raw.access_role,
    hasCustomerPermission: raw.has_customer_permission,
    canOpenCustomer: raw.can_open_customer,
    canEdit: raw.can_edit,
    canAssign: raw.can_assign,
    canDelete: raw.can_delete,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

/** 映射客户工作台自然语言查询解析结果。 */
export function mapCustomerWorkbenchNlQueryParseResult(
  raw: CustomerWorkbenchNlQueryParseResultRaw,
): CustomerWorkbenchNlQueryParseResult {
  return {
    query: raw.query,
    executable: raw.executable,
    conditions: (raw.conditions ?? []).map((condition) => ({
      field: condition.field,
      operator: condition.operator,
      value: condition.value ?? undefined,
      rawText: condition.raw_text ?? undefined,
      label: condition.label ?? undefined,
    })),
    summary: raw.summary ?? [],
    messages: raw.messages ?? [],
  };
}

/** 映射客户销售分配确认结果。 */
export function mapCustomerAssignmentConfirmResult(
  raw: CustomerWorkbenchAssignmentConfirmResultRaw,
): CustomerAssignmentConfirmResult {
  return {
    updatedCount: raw.updated_count,
    skippedCount: raw.skipped_count,
    logCount: raw.log_count,
  };
}

/** 映射客户联系人抽屉分页。 */
export function mapCustomerWorkbenchContactListPage(
  raw: CustomerWorkbenchContactListPageRaw,
): CustomerWorkbenchContactListPage {
  return {
    items: (raw.items ?? []).map(mapCustomerWorkbenchContactItem),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 映射客户联系人抽屉项。 */
export function mapCustomerWorkbenchContactItem(
  raw: CustomerWorkbenchContactRaw,
): CustomerWorkbenchContactItem {
  return {
    id: raw.id,
    contactName: raw.contact_name,
    positionName: raw.position_name ?? undefined,
    mobile: raw.mobile ?? undefined,
    wechat: raw.wechat ?? undefined,
    orgUnitName: raw.org_unit_name ?? undefined,
    orgUnitPathName: raw.org_unit_path_name ?? undefined,
    primaryOwnerUserNames: normalizeStringList(raw.primary_owner_user_names),
    sharedOwnerUserNames: normalizeStringList(raw.shared_owner_user_names),
    latestRecordContent: raw.latest_record_content ?? undefined,
    canOpenContact: raw.can_open_contact,
    canViewSensitiveContactInfo: raw.can_view_sensitive_contact_info,
  };
}

/** 映射客户详情。 */
export function mapCustomerWorkbenchDetail(
  raw: CustomerWorkbenchDetailRaw,
): CustomerWorkbenchDetail {
  return {
    vectorSyncWarning: raw.vector_sync_warning ?? undefined,
    id: raw.id,
    customerName: raw.customer_name,
    orgId: raw.org_id,
    orgName: raw.org_name,
    anchorOrgUnitId: raw.anchor_org_unit_id ?? undefined,
    anchorOrgUnitName: raw.anchor_org_unit_name ?? undefined,
    anchorOrgUnitPathName: raw.anchor_org_unit_path_name ?? undefined,
    anchorOrgUnitType: raw.anchor_org_unit_type ?? undefined,
    anchorLevel: raw.anchor_level,
    coverageMode: raw.coverage_mode,
    businessStatus: raw.business_status,
    customerStar: raw.customer_star ?? undefined,
    isDisabled: raw.is_disabled,
    remark: raw.remark ?? undefined,
    updatedAt: raw.updated_at,
  };
}

/** 映射客户工作台员工候选。 */
function mapCustomerWorkbenchStaffOption(
  raw: NonNullable<CustomerWorkbenchMetadataRaw["staff_options"]>[number],
): CustomerWorkbenchStaffOption {
  return {
    id: raw.id,
    username: raw.username,
    displayName: raw.display_name,
    phone: raw.phone ?? undefined,
    disabled: raw.disabled,
  };
}

/** 映射客户工作台学校候选项。 */
export function mapCustomerWorkbenchOrganizationOption(
  raw: CustomerWorkbenchOrganizationOptionRaw,
): CustomerWorkbenchOrganizationOption {
  return {
    id: raw.id,
    orgName: raw.org_name,
    province: raw.province ?? undefined,
    city: raw.city ?? undefined,
  };
}

/** 映射客户工作台院系专业候选项。 */
export function mapCustomerWorkbenchOrganizationUnitOption(
  raw: CustomerWorkbenchOrganizationUnitOptionRaw,
): CustomerWorkbenchOrganizationUnitOption {
  return {
    id: raw.id,
    orgId: raw.org_id,
    unitName: raw.unit_name,
    unitPathName: raw.unit_path_name,
    levelNo: raw.level_no,
    unitType: raw.unit_type ?? undefined,
  };
}

/** 归一化可选查询文本。 */
function normalizeOptionalText(value?: string | null) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

/** 兼容后端联系人行聚合字符串与响应项数组两种形态。 */
function normalizeStringList(value?: string | string[] | null) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  return normalizeOptionalText(value)
    ?.split(/[、,，]/)
    .map((item) => item.trim())
    .filter(Boolean) ?? [];
}
